/**
 * periodWindows — Ventanas de periodo calendario para los gráficos de Métricas.
 *
 * QUÉ HACE: dado un periodo (Hoy/Semana/Mes/Año) y una fecha de referencia,
 *   devuelve la ventana calendario que representa (día, semana Lun→Dom, mes o
 *   año) y helpers puros para navegar ‹ › entre periodos:
 *   - `resolvePeriodWindow`: rango [from, to] del periodo.
 *   - `shiftPeriodReference`: mueve la referencia ±1 día/semana/mes/año.
 *   - `canShiftPeriodForward`: true si el siguiente periodo ya empezó (no se
 *     navega al futuro: el periodo actual es el último disponible).
 *
 * QUÉ INVOCA: date-fns (startOf/endOf y add*).
 * QUÉ EXPORTA: ChartWindowPeriod, PeriodWindow, resolvePeriodWindow,
 *   shiftPeriodReference, canShiftPeriodForward.
 * QUÉ NO DEBE HACER: UI, storage ni fetch; son cálculos puros de fechas.
 */
import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from "date-fns";

/** Periodos con ventana calendario (los mismos de los gráficos del Resumen). */
export type ChartWindowPeriod = "hoy" | "semana" | "mes" | "anio";

/** Rango inclusivo [from, to] de un periodo. */
export interface PeriodWindow {
  from: Date;
  to: Date;
}

/** Ventana calendario del periodo para la fecha de referencia. */
export function resolvePeriodWindow(
  period: ChartWindowPeriod,
  reference: Date,
): PeriodWindow {
  switch (period) {
    case "hoy":
      return { from: startOfDay(reference), to: endOfDay(reference) };
    case "semana":
      return {
        from: startOfWeek(reference, { weekStartsOn: 1 }),
        to: endOfWeek(reference, { weekStartsOn: 1 }),
      };
    case "mes":
      return { from: startOfMonth(reference), to: endOfMonth(reference) };
    default:
      return { from: startOfYear(reference), to: endOfYear(reference) };
  }
}

/** Mueve la fecha de referencia ±`delta` periodos (día/semana/mes/año). */
export function shiftPeriodReference(
  period: ChartWindowPeriod,
  reference: Date,
  delta: number,
): Date {
  switch (period) {
    case "hoy":
      return addDays(reference, delta);
    case "semana":
      return addWeeks(reference, delta);
    case "mes":
      return addMonths(reference, delta);
    default:
      return addYears(reference, delta);
  }
}

/**
 * true si el periodo siguiente ya empezó (permite avanzar). El periodo actual es
 * el último navegable: no se muestra el futuro.
 */
export function canShiftPeriodForward(
  period: ChartWindowPeriod,
  reference: Date,
): boolean {
  const next = resolvePeriodWindow(
    period,
    shiftPeriodReference(period, reference, 1),
  );
  return next.from.getTime() <= Date.now();
}
